// The field from above (K.field and its parts), in yards. The field changes over for a left-hander: every position and zone mirrors in x.
import { C, esc, uid, type BallPath, type OvalInput, type Position, type Zone } from './types';
import { BOWLER, KEEPER, KEEPER_UP, NAMES, NON_STRIKER, POSITIONS, STRIKER, ZONE_ANGLES, type XY } from './positions';

const RX = 66, RY = 63;
const f1 = (n: number) => n.toFixed(1);

export const dir = (d: number): XY => [Math.sin((d * Math.PI) / 180), Math.cos((d * Math.PI) / 180)];
export const at = (d: number, dist: number): XY => { const v = dir(d); return [STRIKER[0] + v[0] * dist, STRIKER[1] + v[1] * dist]; };
/** Where the line from `from` through `to` meets the rope. */
export const rope = (to: XY, from: XY = STRIKER): XY => {
  const [x0, y0] = from, dx = to[0] - x0, dy = to[1] - y0;
  const a = (dx * dx) / (RX * RX) + (dy * dy) / (RY * RY), b = 2 * ((x0 * dx) / (RX * RX) + (y0 * dy) / (RY * RY)), c = (x0 * x0) / (RX * RX) + (y0 * y0) / (RY * RY) - 1;
  const t = (-b + Math.sqrt(b * b - 4 * a * c)) / (2 * a);
  return [x0 + dx * t, y0 + dy * t];
};
export const edge = (d: number): XY => rope(at(d, 10));
/** A position's spot, mirrored in x when a left-hander is on strike. */
export const spot = (p: Position, left = false): XY => (left ? [-POSITIONS[p][0], POSITIONS[p][1]] : POSITIONS[p]);
/** A zone's wedge in degrees, mirrored for a left-hander. */
export const zoneArc = (z: Zone, left = false): [number, number] => (left ? [-ZONE_ANGLES[z][1], -ZONE_ANGLES[z][0]] : ZONE_ANGLES[z]);
export const zoneMid = (z: Zone, left = false): number => { const [a, b] = zoneArc(z, left); return (a + b) / 2; };
const translate = (p: XY) => `translate(${p[0]} ${p[1]})`;

export interface Tok { id?: string; at: XY; ini?: string; color: string; hi?: boolean; name?: string; badge?: string }
export interface ShotPath { from?: XY; to: XY; air?: boolean; runs?: number; out?: boolean; color?: string; w?: number; noEnd?: boolean }
export interface FieldOpts {
  label?: string; pad?: number; tok?: number; zones?: Zone[]; names?: boolean; left?: boolean;
  moves?: [XY, XY][]; paths?: ShotPath[]; glow?: XY | null; fielders?: Tok[]; batters?: Tok[];
}

function zones(z: Zone[], id: string, left: boolean): string {
  const [ox, oy] = STRIKER, R = 140;
  let s = `<g clip-path="url(#c${id})" class="zones">`;
  if (z.length) (Object.keys(ZONE_ANGLES) as Zone[]).forEach(k => {
    const pref = z.includes(k);
    if (k === 'Cordon' && !pref) return;
    const [a1, a2] = zoneArc(k, left), p1 = dir(a1), p2 = dir(a2);
    s += `<path class="zone${pref ? ' pref' : ''}" data-zone="${k}" d="M${ox} ${oy}L${f1(ox + p1[0] * R)} ${f1(oy + p1[1] * R)}A${R} ${R} 0 0 0 ${f1(ox + p2[0] * R)} ${f1(oy + p2[1] * R)}Z" fill="${pref ? 'rgba(240,200,70,.32)' : 'rgba(0,0,0,0)'}" stroke="rgba(234,243,234,.18)" stroke-width=".4"/>`;
  });
  return s + '</g>';
}

/** K.shot: a ball path, curved and dashed in the air, a cross at the end when out. */
export function shot(p: ShotPath): string {
  const a = p.from || STRIKER, b = p.to, runs = p.runs ?? 0;
  const col = p.color || (p.out ? C.bad : runs >= 6 ? C.goldL : runs === 4 ? '#e8c55e' : '#d6e7d6');
  const w = p.w || (runs >= 4 ? 1.05 : 0.6);
  let d;
  if (p.air) {
    const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2, dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy) || 1, k = L * 0.14;
    d = `M${a[0]} ${a[1]}Q${f1(mx - (dy / L) * k)} ${f1(my + (dx / L) * k)} ${f1(b[0])} ${f1(b[1])}`;
  } else d = `M${a[0]} ${a[1]}L${f1(b[0])} ${f1(b[1])}`;
  let s = `<path d="${d}" fill="none" stroke="${col}" stroke-width="${w}" stroke-linecap="round"${p.air ? ' stroke-dasharray="2.2 1.4"' : ''}/>`;
  if (p.out) s += `<path class="out" d="M${f1(b[0] - 1.9)} ${f1(b[1] - 1.9)}l3.8 3.8m0 -3.8l-3.8 3.8" stroke="${C.bad}" stroke-width="1" stroke-linecap="round"/>`;
  else if (!p.noEnd) s += `<circle cx="${f1(b[0])}" cy="${f1(b[1])}" r="${runs >= 4 ? 1.15 : 0.8}" fill="${col}"/>`;
  return s;
}

/** K.token, drawn about its own origin so a transform can move it. */
function token(f: Tok, r: number, names?: boolean): string {
  let s = `<g class="tok"${f.id ? ` data-id="${esc(f.id)}"` : ''} transform="${translate(f.at)}">`;
  s += `<circle r="${r}" fill="${f.color}" stroke="${f.hi ? C.goldL : C.ink}" stroke-width="${f.hi ? 1 : 0.45}"/>`;
  if (f.ini) s += `<text y="${(r * 0.34).toFixed(2)}" text-anchor="middle" font-size="${(r * 0.84).toFixed(2)}" font-weight="800" fill="${C.ink}">${esc(f.ini)}</text>`;
  if (names && f.name) s += `<text y="${f1(r + 3)}" text-anchor="middle" font-size="2.5" fill="#d6e7d6" opacity=".78">${f.name}</text>`;
  return s + '</g>';
}

/** K.batter: a diamond, also moved by its transform. A badge (an L for a left-hander) sits at its top right. */
function batter(b: Tok, r: number): string {
  const q = r * 1.08;
  let s = `<g class="bat"${b.id ? ` data-id="${esc(b.id)}"` : ''} transform="${translate(b.at)}"><path d="M0 ${-q}L${q} 0L0 ${q}L${-q} 0Z" fill="${b.color}" stroke="${C.ink}" stroke-width=".45"/>`;
  if (b.ini) s += `<text y="${(r * 0.3).toFixed(2)}" text-anchor="middle" font-size="${(r * 0.72).toFixed(2)}" font-weight="800" fill="${C.ink}">${esc(b.ini)}</text>`;
  if (b.badge) s += `<g class="badge" transform="translate(${f1(q * 0.9)} ${f1(-q * 0.9)})"><circle r="${f1(r * 0.42)}" fill="${C.goldL}" stroke="${C.ink}" stroke-width=".3"/><text y="${(r * 0.15).toFixed(2)}" text-anchor="middle" font-size="${(r * 0.5).toFixed(2)}" font-weight="800" fill="${C.ink}">${esc(b.badge)}</text></g>`;
  return s + '</g>';
}

/** K.field. `id` names the defs; an update passes the old one so the new layers still resolve. */
export function field(o: FieldOpts, id = uid()): string {
  const pad = o.pad ?? 5, W = RX + pad, H = RY + pad, r = o.tok || 3.4, left = !!o.left;
  let s = `<svg class="field${left ? ' left' : ''}" data-uid="${id}" viewBox="${-W} ${-H} ${2 * W} ${2 * H}" role="img" aria-label="${esc(o.label || 'Cricket field from above')}">`;
  s += `<defs><clipPath id="c${id}"><ellipse rx="${RX}" ry="${RY}"/></clipPath><radialGradient id="v${id}"><stop offset=".6" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".42"/></radialGradient><filter id="b${id}" x="-1" y="-1" width="3" height="3"><feGaussianBlur stdDeviation="1.8"/></filter><marker id="m${id}" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="5" markerHeight="5" orient="auto"><path d="M0 1L9 5L0 9z" fill="${C.goldL}"/></marker></defs>`;
  s += `<rect x="${-W}" y="${-H}" width="${2 * W}" height="${2 * H}" rx="${pad + 3}" fill="#0a150b"/>`;
  s += `<g clip-path="url(#c${id})"><rect x="-80" y="-80" width="160" height="160" fill="#1a3f21"/>`;
  for (let y = -77; y < 80; y += 14) s += `<rect x="-80" y="${y}" width="160" height="7" fill="#1d4625"/>`;
  s += `<ellipse rx="${RX}" ry="${RY}" fill="url(#v${id})"/></g>`;
  s += zones(o.zones || [], id, left);
  s += `<ellipse rx="${RX}" ry="${RY}" fill="none" stroke="#eaf3ea" stroke-opacity=".85" stroke-width=".8"/>`;
  s += `<path d="M-30 -11A30 30 0 0 1 30 -11L30 11A30 30 0 0 1 -30 11Z" fill="none" stroke="#eaf3ea" stroke-opacity=".42" stroke-width=".5" stroke-dasharray="1.8 1.5"/>`;
  s += `<rect x="-1.7" y="-12.8" width="3.4" height="25.6" rx=".5" fill="#c4ab78"/><path d="M-2.3 -9.7H2.3M-2.3 9.7H2.3M-1.45 -11H1.45M-1.45 11H1.45" stroke="#fff" stroke-width=".22"/><rect x="-.55" y="-11.3" width="1.1" height=".6" fill="#4a3518"/><rect x="-.55" y="10.7" width="1.1" height=".6" fill="#4a3518"/>`;
  s += '<g class="moves">';
  (o.moves || []).forEach(([a, b]) => {
    const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (d < r * 2) return;
    const k0 = (r + 0.6) / d, k1 = (d - r - 1.4) / d;
    s += `<path d="M${f1(a[0] + (b[0] - a[0]) * k0)} ${f1(a[1] + (b[1] - a[1]) * k0)}L${f1(a[0] + (b[0] - a[0]) * k1)} ${f1(a[1] + (b[1] - a[1]) * k1)}" stroke="${C.goldL}" stroke-width=".6" stroke-dasharray="1.4 1" marker-end="url(#m${id})" opacity=".95"/>`;
  });
  s += '</g><g class="ball">';
  (o.paths || []).forEach(p => (s += shot(p)));
  if (o.glow) { const g = rope(o.glow); s += `<circle cx="${f1(g[0])}" cy="${f1(g[1])}" r="6" fill="${C.goldL}" opacity=".85" filter="url(#b${id})"/><circle cx="${f1(g[0])}" cy="${f1(g[1])}" r="1.4" fill="#fff"/>`; }
  s += '</g>';
  (o.fielders || []).forEach(f => (s += token(f, r, o.names)));
  (o.batters || []).forEach(b => (s += batter(b, r)));
  return s + '</svg>';
}

/** Where a fielding athlete stands in this field, or null when they are not in it. */
export function spotOf(input: OvalInput, id: string): XY | null {
  const f = input.fielders.find(p => p.id === id);
  if (f) return spot(f.position, input.leftHanded);
  if (id === input.keeper.id) return input.keeperUp ? KEEPER_UP : KEEPER;
  if (id === input.bowler.id) return BOWLER;
  return null;
}

/** The end of the ball path: the rope when boundary, the fielder's spot when fielded, else 40 yards into the zone. */
export function ballEnd(input: OvalInput, b: BallPath): XY | null {
  if (b.boundary != null && b.zone) return edge(zoneMid(b.zone, input.leftHanded));
  const fielded = b.fielder ? spotOf(input, b.fielder) : null;
  if (fielded) return fielded;
  return b.zone ? at(zoneMid(b.zone, input.leftHanded), 40) : null;
}

/** Every token of the oval: the fielding side (the nine, the keeper, the bowler) and the two batters. */
function tokens(input: OvalInput): { fielders: Tok[]; batters: Tok[] } {
  const hi = input.ball?.fielder ?? null;
  const side = (id: string, ini: string, at: XY, name: string): Tok => ({ id, ini, at, name, color: C.valley, hi: id === hi });
  return {
    fielders: [
      ...input.fielders.map(f => side(f.id, f.initials, spot(f.position, input.leftHanded), NAMES[f.position])),
      side(input.keeper.id, input.keeper.initials, input.keeperUp ? KEEPER_UP : KEEPER, NAMES[input.keeperUp ? 'keeperUp' : 'keeper']),
      side(input.bowler.id, input.bowler.initials, BOWLER, NAMES.bowler),
    ],
    batters: [
      { id: input.striker.id, ini: input.striker.initials, at: STRIKER, color: C.harbor, badge: input.leftHanded ? 'L' : undefined },
      { id: input.nonStriker.id, ini: input.nonStriker.initials, at: NON_STRIKER, color: C.harbor },
    ],
  };
}

/** The transform of every token by athlete id: the pure half of updateOval. */
export function tokenTransforms(input: OvalInput): Record<string, string> {
  const t = tokens(input);
  return Object.fromEntries([...t.fielders, ...t.batters].map(k => [k.id!, translate(k.at)]));
}

export function drawOval(input: OvalInput, id?: string): string {
  const b = input.ball, end = b && ballEnd(input, b);
  return field({
    label: input.label,
    zones: input.zones,
    names: input.names,
    left: input.leftHanded,
    moves: input.moves.map(m => [spot(m.from, input.leftHanded), spot(m.to, input.leftHanded)]),
    paths: b && end ? [{ to: end, air: b.aerial, runs: b.runs, out: b.out }] : [],
    glow: b && end && b.boundary != null ? end : null,
    ...tokens(input),
  }, id);
}

/**
 * Draws the oval into el. When el already shows the same athletes, it moves each token by its transform
 * (the CSS transition animates it) and swaps the zones, arrows and ball path in place. A change of hand
 * moves every token, so the field changing over animates like any other move.
 */
export function updateOval(el: HTMLElement, input: OvalInput): void {
  const svg = el.querySelector<SVGSVGElement>('svg.field');
  const moved = tokenTransforms(input);
  const olds = svg ? Array.from(svg.querySelectorAll<SVGGElement>('[data-id]')) : [];
  if (!svg || olds.length !== Object.keys(moved).length || !olds.every(g => g.dataset.id! in moved)) {
    el.innerHTML = drawOval(input);
    return;
  }
  const t = document.createElement('template');
  t.innerHTML = drawOval(input, svg.dataset.uid);
  const fresh = t.content.querySelector('svg')!;
  for (const g of olds) {
    const id = g.dataset.id!;
    g.setAttribute('transform', moved[id]);
    g.innerHTML = fresh.querySelector(`[data-id="${CSS.escape(id)}"]`)!.innerHTML;
  }
  for (const layer of ['zones', 'moves', 'ball']) svg.querySelector(`g.${layer}`)!.replaceWith(fresh.querySelector(`g.${layer}`)!);
  svg.setAttribute('aria-label', input.label);
  svg.classList.toggle('left', input.leftHanded);
}
